<template>
  <div
    class="oct-select"
    :class="[{
      'oct-select--block': block,
      'oct-select--disabled': disabled
    }]"
  >
    <div class="oct-select__anchor">
      <select
        class="oct-select__native-input"
        :disabled="disable"
        v-model="$_octSelect_value"
      >
        <option
          v-for="(item, i) in items"
          :key="i"
          :value="item.value"
          :disabled="item.disabled"
        >
          {{ item.text }}
        </option>
      </select>
      <span class="oct-select__dropdown-icon">
        <oct-icon icon="expand-more" />
      </span>
    </div>

    <div class="oct-oct-select-helper-line" v-if="helperText !== '' || (invalid && helperTextInvalid !== '')">
      <span class="oct-oct-select-helper-text" aria-hidden="true">{{ invalid ? helperTextInvalid : helperText }}</span>
    </div>
  </div>
</template>

<script>
import OctIcon from '@/components/icon/OctIcon.vue';

export default {
  name: 'OctSelect',
  components: { OctIcon },
  props: {
    /** Selected Value */
    value: {
      type: String,
      default: ''
    },
    /**
     * Options
     * @property {string} items[].text Display text
     * @property {string} items[].value The value
     * @property {boolean} items[].disabled The option is disabled if true
     */
    items: {
      type: Array,
      default () {
        return []
      }
    },
    /** Helper text */
    helperText: {
      type: String,
      default: ''
    },
    /** Invalid helper text */
    helperTextInvalid: {
      type: String,
      default: ''
    },
    /** Display block mode */
    block: {
      type: Boolean,
      default: false
    },
    /** External validation function */
    validator: {
      type: Function,
      default: null
    },
    /** Native select disabled value */
    disable: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      invalid: false
    }
  },
  computed: {
    /**
     * Whether current selected option is disabled
     */
    disabled () {
      // selected value
      const selected = this.items.filter(item => item.value === this.$_octSelect_value)

      if (selected.length)
        return selected[0].disabled || false

      // nothing is selected
      return false
    },
    $_octSelect_value: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  methods: {
    /** Input validation */
    async validate (val) {
      // Empty check
      let invalid = this.required && !val

      // Number check
      invalid = (this.type === 'number' && !this.validateNumber(val)) || invalid

      // Email check
      invalid = (this.type === 'email' && !this.validateEmail(val)) || invalid

      // Finally do the external validation
      if (!invalid && this.validator)
        invalid = !await this.validator(val)

      this.invalid = invalid

      return !invalid
    },
  }
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-select {
  display: inline-block;

  &--block {
    display: block;
    width: 100%;

    .oct-select__native-input {
      width: 100%;
    }
  }
}

.oct-select__anchor {
  position: relative;
}

.oct-select__native-input {
  font-family: $font-family-sans-serif-ja;
  font-size: oct-rem(18);
  font-weight: 400;
  line-height: (24/18);
  color: $oct-theme--primary;
  margin: 0;
  padding: oct-rem(17-5-1) oct-rem(44-1) oct-rem(17-5-1) oct-rem(16-1);
  background-color: transparent;
  border: 1px solid $oct-theme--primary;
  border-radius: oct-rem(8);
  outline: none;
  box-sizing: border-box;
  appearance: none;
  overflow: hidden;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0,0,0,0);

  .oct-select--disabled & {
    color: rgba(0,0,0,.55);
  }

  &:hover, &:focus {
    padding: oct-rem(17-5-2) oct-rem(44-2) oct-rem(17-5-2) oct-rem(16-2);
    border-width: oct-rem(2);
  }

  &[disabled] {
    opacity: .4;
    pointer-events: none;
  }

  option:disabled {
    opacity: .55;
  }
}

.oct-select__dropdown-icon {
  position: absolute;
  top: 50%;
  right: oct-rem(17-4);
  width: oct-rem(20);
  height: oct-rem(20);
  transform: translateY(-50%);
  pointer-events: none;

  .oct-icon {
    width: 100%;
    height: 100%;
  }

  .oct-select__native-input[disabled] + & {
    display: none;
  }
}

.oct-oct-select-helper-line {
  opacity: .55;

  [aria-invalid="true"] + & {
    color: $oct-theme--danger;
    opacity: 1;
  }
}

.oct-oct-select-helper-text {
  font-size: oct-rem(13);
  line-height: (14/13);
  margin: (-2em/13) 0;
  display: block;
}
</style>
